import { useState } from 'react';
import { TabelaCartoes } from '@/components/common/TabelaCartoes';
import { ListaMobile, ItemLista, Selo } from '@/components/common/ListaMobile';
import { FolderOpen, Trash2, Loader2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { orcamentosApi } from '@/api/endpoints/orcamentos';
import type { OrcamentoStatus } from '@/api/endpoints/orcamentos';
import { useOrcamentosSalvos, useDeletarOrcamento } from '@/hooks/useOrcamentos';
import type { OrcamentoDraftData } from './draftStore';
import { brl } from './calc';

const card = 'rounded-xl border border-border bg-bg-elevated p-4';

// `cls` pinta o selo da tabela (desktop); `tom` é o mesmo tom no Selo da lista mobile.
const STATUS_META: Record<
  OrcamentoStatus,
  { label: string; cls: string; tom: 'neutro' | 'info' | 'destaque' }
> = {
  rascunho: { label: 'Rascunho', cls: 'bg-muted text-text-secondary', tom: 'neutro' },
  enviado: { label: 'Enviado', cls: 'bg-primary/15 text-primary', tom: 'info' },
  vendido: { label: 'Vendido', cls: 'bg-tertiary/15 text-tertiary', tom: 'destaque' },
};

const fmtData = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

// Data curta (17/10) para a linha de apoio da lista mobile.
const fmtDataCurta = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  } catch {
    return iso;
  }
};

export function HistoricoTab({
  onAbrir,
}: {
  onAbrir: (dados: OrcamentoDraftData, id: number) => void;
}) {
  const listQ = useOrcamentosSalvos();
  const del = useDeletarOrcamento();
  const [loadingId, setLoadingId] = useState<number | null>(null);

  const abrir = async (id: number) => {
    setLoadingId(id);
    try {
      const o = await orcamentosApi.getSalvo(id);
      onAbrir(o.dados, o.id);
    } finally {
      setLoadingId(null);
    }
  };

  const excluir = (id: number, nome: string) => {
    if (confirm(`Excluir o orçamento de "${nome || 'sem nome'}"?`)) del.mutate(id);
  };

  const orcamentos = listQ.data ?? [];
  // Carregando / vazio: a mesma mensagem nas duas versões da lista.
  const aviso = listQ.isLoading
    ? 'Carregando…'
    : orcamentos.length === 0
      ? 'Nenhum orçamento salvo ainda. Monte um em “Novo orçamento” e clique em “Salvar orçamento”.'
      : null;

  return (
    <div>
      {/* Celular e tablet: tocar na linha abre o orçamento (mesma ação do botão "Abrir") */}
      <ListaMobile className="lg:hidden">
        {aviso ? (
          <li className="px-4 py-8 text-center text-sm text-text-secondary">{aviso}</li>
        ) : (
          orcamentos.map((o) => {
            const cidade = o.clienteCidade
              ? `${o.clienteCidade}${o.clienteUf ? `/${o.clienteUf}` : ''}`
              : '';
            return (
              <ItemLista
                key={o.id}
                onClick={() => {
                  if (loadingId === null) void abrir(o.id);
                }}
                titulo={o.clienteNome || <span className="text-text-secondary">Sem nome</span>}
                apoio={
                  <>
                    <Selo tom={STATUS_META[o.status].tom} className="mr-1.5 align-middle">
                      {STATUS_META[o.status].label}
                    </Selo>
                    {[cidade, fmtDataCurta(o.updatedAt)].filter(Boolean).join(' · ')}
                  </>
                }
                fim={
                  loadingId === o.id ? (
                    <Loader2 size={18} className="animate-spin text-text-hint" />
                  ) : (
                    <span className="text-[15px] font-bold tabular-nums text-text-title">
                      {brl(o.totalGeral)}
                    </span>
                  )
                }
                acao={
                  <button
                    type="button"
                    onClick={() => excluir(o.id, o.clienteNome)}
                    aria-label={`Excluir orçamento de ${o.clienteNome || 'sem nome'}`}
                    className="flex size-10 items-center justify-center rounded-lg text-text-secondary transition-colors hover:text-error active:bg-muted/60"
                  >
                    <Trash2 size={17} />
                  </button>
                }
              />
            );
          })
        )}
      </ListaMobile>

      <div className={card + ' hidden overflow-x-auto lg:block'}>
        <p className="mb-3 text-sm text-text-secondary">
          Orçamentos salvos. Clique em “Abrir” para continuar/editar sem refazer do zero.
        </p>

        {aviso ? (
          <p className="py-8 text-center text-sm text-text-secondary">{aviso}</p>
        ) : (
          <TabelaCartoes className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-text-secondary">
                <th className="py-2">Cliente</th>
                <th className="py-2">Status</th>
                <th className="py-2 text-right">Total</th>
                <th className="py-2">Atualizado</th>
                <th className="w-32"></th>
              </tr>
            </thead>
            <tbody>
              {orcamentos.map((o) => (
                <tr key={o.id} className="border-b border-border/60">
                  <td className="py-2 text-text-title">
                    {o.clienteNome || <span className="text-text-secondary">Sem nome</span>}
                    {o.clienteCidade ? (
                      <span className="text-text-secondary">
                        {' '}
                        · {o.clienteCidade}
                        {o.clienteUf ? `/${o.clienteUf}` : ''}
                      </span>
                    ) : null}
                  </td>
                  <td className="py-2">
                    <span
                      className={`rounded-md px-2 py-0.5 text-xs font-medium ${STATUS_META[o.status].cls}`}
                    >
                      {STATUS_META[o.status].label}
                    </span>
                  </td>
                  <td className="py-2 text-right font-medium text-text-title">
                    {brl(o.totalGeral)}
                  </td>
                  <td className="py-2 text-text-secondary">
                    <span className="inline-flex items-center gap-1">
                      <Clock size={12} /> {fmtData(o.updatedAt)}
                    </span>
                  </td>
                  <td className="py-2">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => abrir(o.id)}
                        disabled={loadingId === o.id}
                      >
                        {loadingId === o.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <FolderOpen size={14} />
                        )}
                        Abrir
                      </Button>
                      <button
                        className="text-text-secondary hover:text-error"
                        onClick={() => excluir(o.id, o.clienteNome)}
                        title="Excluir"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </TabelaCartoes>
        )}
      </div>
    </div>
  );
}
